/* Shared accessibility helpers for every Windly overlay (menu, cart drawer, popup, quiz).
   Exposed on window.Wly so sections can use them without a module bundler. */

(function () {
  const FOCUSABLE = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'details > summary',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');

  let trapState = null;

  const visibleFocusable = (container) =>
    Array.from(container.querySelectorAll(FOCUSABLE)).filter(
      (el) => el.offsetWidth > 0 || el.offsetHeight > 0 || el === document.activeElement
    );

  function onKeydown(event) {
    if (!trapState) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      trapState.onEscape?.();
      return;
    }
    if (event.key !== 'Tab') return;

    const items = visibleFocusable(trapState.container);
    if (items.length === 0) return;
    const first = items[0];
    const last = items[items.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function trapFocus(container, { onEscape, initial } = {}) {
    releaseFocus();
    trapState = { container, onEscape, previous: document.activeElement };
    document.addEventListener('keydown', onKeydown);
    const target = initial || visibleFocusable(container)[0] || container;
    // Wait a frame so the container has finished becoming visible.
    requestAnimationFrame(() => target.focus({ preventScroll: true }));
  }

  function releaseFocus({ restore = true } = {}) {
    if (!trapState) return;
    document.removeEventListener('keydown', onKeydown);
    const previous = trapState.previous;
    trapState = null;
    if (restore && previous && typeof previous.focus === 'function') {
      previous.focus({ preventScroll: true });
    }
  }

  let scrollLocks = 0;
  let savedScrollY = 0;

  function lockScroll() {
    scrollLocks += 1;
    if (scrollLocks > 1) return;
    savedScrollY = window.scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${savedScrollY}px`;
    document.body.style.width = '100%';
  }

  function unlockScroll() {
    scrollLocks = Math.max(0, scrollLocks - 1);
    if (scrollLocks > 0) return;
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    window.scrollTo(0, savedScrollY);
  }

  /* Formats cents using the shop's own money_format string, so prices rendered by
     JavaScript match the ones Liquid renders exactly. */
  function money(cents, format = '${{amount}}') {
    const value = (cents || 0) / 100;
    const group = (n, thousands, decimal, places) => {
      const [int, frac] = n.toFixed(places).split('.');
      const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return places > 0 ? `${grouped}${decimal}${frac}` : grouped;
    };

    return String(format).replace(/\{\{\s*(\w+)\s*\}\}/g, (_, token) => {
      switch (token) {
        case 'amount_no_decimals':
          return group(Math.round(value), ',', '.', 0);
        case 'amount_with_comma_separator':
          return group(value, '.', ',', 2);
        case 'amount_no_decimals_with_comma_separator':
          return group(Math.round(value), '.', ',', 0);
        case 'amount_with_apostrophe_separator':
          return group(value, "'", '.', 2);
        case 'amount_with_space_separator':
          return group(value, ' ', ',', 2);
        case 'amount_no_decimals_with_space_separator':
          return group(Math.round(value), ' ', ',', 0);
        default:
          return group(value, ',', '.', 2);
      }
    });
  }

  window.Wly = Object.assign(window.Wly || {}, {
    FOCUSABLE,
    trapFocus,
    releaseFocus,
    lockScroll,
    unlockScroll,
    money,
  });
})();
